import {
	Component,
	Output,
	EventEmitter,
	ElementRef,
	ViewChild,
	Input,
	ContentChildren,
	QueryList,
	AfterContentChecked,
	OnInit,
} from '@angular/core';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SortableHeaderColumn } from '../sortableHeaderColumn/sortableHeaderColumn.component';
import { PagedQueryParameters, PagedQueryResult, ITableService, Sorting } from '../generalInterfaces';
import { SortEventEmitterService } from '../sortableHeaderColumn/sortEventEmitter.service';
import { OrderByPipe } from '../orderBy.pipe';
import { ContextParams } from '../../core/globalSettings.service';
import * as AT from '../actionTypes';
import { EupObserver } from '../../core/eupObserver.service';
import { SpinnerService } from '../../core/spinner/spinner.service';
import { PlatformDetectorService } from '@core/platformDetector.service';
import { BaseDestroyable } from '@core/base-destroyable';

@Component({
	selector: 'eup-tbl',
	templateUrl: './table.component.html',
	providers: [SortEventEmitterService],
	styleUrls: ['./table.component.scss'],
})
export class TableComponent extends BaseDestroyable implements AfterContentChecked, OnInit {
	@Output() onBeforeLoadingRows = new EventEmitter();
	@Output() onRowsChanged = new EventEmitter();
	@Output() onRowClicked = new EventEmitter<Element>();
	@Input() pagingOnScroll = false;
	@Input() preventOverflowX = false;
	@Input() shouldActiveRow = true;
	isScrollable = false;
	el: ElementRef;

	@ContentChildren(SortableHeaderColumn, { descendants: true }) private cols: QueryList<SortableHeaderColumn>;
	@ViewChild('innerTable') private innerTable: ElementRef;
	@ViewChild('innerTableDiv') private innerTableDiv: ElementRef;

	pagingParams = new PagedQueryParameters();
	shouldGetMoreData = true;
	hasAllData = false;
	pagingService: ITableService;
	queryResult: PagedQueryResult;
	isFinishedDataBinding: boolean;

	constructor(
		private emitter: SortEventEmitterService,
		public platformDetector: PlatformDetectorService,
		el: ElementRef,
		private eupObserver: EupObserver,
		private spinnerService: SpinnerService
	) {
		super();
		this.el = el;
		this.emitter.sortClicked$.pipe(takeUntil(this.componentAlive$)).subscribe((sortParams: PagedQueryParameters) => {
			this.onSort(sortParams);
		});
	}

	ngOnInit() {
		new Observable<any>(observer => {
			const subscription = this.eupObserver.subscribe(observer);

			return () => subscription.unsubscribe();
		})
			.pipe(takeUntil(this.componentAlive$))
			.subscribe(value => {
				switch (true) {
					case value.action === AT.EDIT_WITH_ITERO_LAB_SUCCESS:
						this.researchRows();
						break;
					case value.action === AT.DOWNLOAD_NOTIFICATION_SERVICE_DOWNLOADED:
						this.researchRows(true);
						break;
					default:
						break;
				}
			});
	}

	// Respond after Angular checks the content projected into the component
	ngAfterContentChecked() {
		const scrollbarWidth = this.getScrollWidth();

		this.isScrollable = scrollbarWidth > 0;
	}

	public showNoResults(): boolean {
		return (
			this.isFinishedDataBinding &&
			this.pagingParams.pageNumber === 1 &&
			(!this.pagingService.getRows() || (this.pagingService.getRows() && !this.pagingService.getRows().length))
		);
	}

	public setTableParams(service: ITableService, initialQuery: PagedQueryParameters) {
		this.pagingParams = initialQuery;
		this.pagingService = service;
	}

	public updateTableContext(params: ContextParams): void {
		this.pagingParams.companyId = params.companyId;
		this.pagingParams.doctorId = params.doctorId;

		this.resetPagedQuery();
		this.searchRows();
	}

	public sortRows() {
		OrderByPipe.prototype.transform(this.pagingService.getRows(), this.pagingParams.sortField, this.pagingParams.sort);
		this.scrollToTop();
		this.onRowsChanged.emit();
	}

	public filterRows(filter: string) {
		// don't do anything if the filter hasn't changed
		if (this.pagingParams.filter === filter) {
			return;
		}

		this.resetPagedQuery();
		this.pagingParams.filter = filter;
		this.searchRows();
	}

	public reload() {
		this.resetPagedQuery();
		this.searchRows();
	}

	public searchRows(isSearchAfterDownload = false, notifyRowsSearchCompleted = false): void {
		this.setColumnsActivation(this.pagingParams.sortField);

		this.isFinishedDataBinding = false;
		this.shouldGetMoreData = false;

		this.onBeforeLoadingRows.emit();

		const requestContext = {
			filter: this.pagingParams.filter,
			pageNumber: this.pagingParams.pageNumber,
			sortField: this.pagingParams.sortField,
			sort: this.pagingParams.sort,
		};

		this.pagingService
			.requestPage(this.pagingParams)
			.pipe(takeUntil(this.componentAlive$))
			.subscribe((queryResult: PagedQueryResult) => {
				if (this.isStaleSearchResponse(requestContext)) {
					return;
				}

				this.searchRowsCompleted(queryResult, requestContext.pageNumber);
				if (isSearchAfterDownload) {
					this.eupObserver.emit({
						action: AT.TABLE_REFRESHED_AFTER_NOTIFICATION_SERVICE_DOWNLOADED,
						payload: null,
					});
				} else if (notifyRowsSearchCompleted) {
					this.eupObserver.emit({
						action: AT.NOTIFY_SEARCH_ROWS_COMPLETED,
						payload: {
							rowsCount: queryResult.results ? queryResult.results.length : 0,
							id: this.pagingService.id,
						},
					});
				}
			});
	}

	/* tslint:disable:no-unused-variable */
	public onScroll() {
		this.pagingParams.pageNumber++;
		this.searchRows();
	}

	public onTableClickedEvent(event: Event): void {
		if (this.shouldActiveRow) {
			const eventTarget = event.target as Element;
			const parentsList = this.getParents(eventTarget, 'tbody');

			if (parentsList.some(elem => elem.localName === 'tbody')) {
				this.onRowClickedEvent(event, parentsList.find(elem => elem.localName === 'tr') as HTMLTableRowElement);
			}
			event.stopPropagation();
		}
	}

	public getInnerTableDiv(): ElementRef {
		return this.innerTableDiv;
	}

	public setCurrentTableTotal(newValue) {
		if (!Number.isNaN(Number(newValue)) || newValue === null) {
			this.pagingParams.existingTotal = newValue;
		}
	}

	public resetCurrentTableTotal() {
		this.pagingParams.existingTotal = this.pagingParams.defaultTotalValue;
	}

	private researchRows(isSearchAfterDownload = false): void {
		this.spinnerService.start();
		setTimeout(() => {
			this.pagingParams.pageNumber = 1;
			this.searchRows(isSearchAfterDownload);
			this.spinnerService.stop();
		}, 10000);
	}

	private searchRowsCompleted(queryResult: PagedQueryResult, requestedPageNumber: number) {
		this.queryResult = queryResult;
		// for new queries (first page) update UI
		// only once we got new data
		if (requestedPageNumber === 1) {
			this.pagingService.getRows().length = 0;
			this.scrollToTop();
		}

		// server returns up to pageSize+1 results, so only add pageSize items to the table data
		Array.prototype.push.apply(this.pagingService.getRows(), queryResult.results.slice(0, this.pagingParams.pageSize));

		// if there are more pages, allow sending additional queries
		if (queryResult.results.length > this.pagingParams.pageSize - 1) {
			this.shouldGetMoreData = true;
			this.hasAllData = false;
		} else {
			// client received all the pages for the current query
			this.hasAllData = true;
		}

		this.isFinishedDataBinding = true;
		this.onRowsChanged.emit();
	}

	private onSort(sortParams: PagedQueryParameters): void {
		this.pagingParams.sort = sortParams.sort;
		this.pagingParams.sortField = sortParams.sortField;
		this.resetPagedQuery();
		this.searchRows();
	}

	private resetPagedQuery(): void {
		this.pagingParams.pageNumber = 1;
		this.hasAllData = false;
	}

	private setColumnsActivation(colNameToSkip: string) {
		this.cols?.forEach(col => {
			col.active = col.name === colNameToSkip;
			if (col.name !== colNameToSkip) {
				col.sortingMode = Sorting.Descending;
			}
		});
	}

	private onRowClickedEvent(event: Event, row: HTMLTableRowElement): void {
		if (!row) {
			return;
		}

		const disableActive = row.classList.contains('active');
		const otherActiveRows = row.parentElement.getElementsByClassName('active');

		if (otherActiveRows.length > 0) {
			otherActiveRows[0].classList.remove('active');
		}

		if (!disableActive) {
			row.classList.add('active');
		}

		this.onRowClicked.emit(row);
	}

	private getParents(el: Element, parentSelector: string): Element[] {
		const parents = [] as Element[];
		let parent = el.parentElement;

		while (parent && parent.localName !== parentSelector) {
			const child = parent;

			parents.push(child);
			parent = child.parentElement;
		}
		if (parent) {
			parents.push(parent); // push that parent I wanted to stop at
		}

		return parents;
	}

	private getScrollWidth(): number {
		if (this.innerTableDiv && this.innerTable) {
			return this.innerTableDiv.nativeElement.offsetWidth - this.innerTable.nativeElement.offsetWidth;
		}

		return 0;
	}

	private scrollToTop() {
		if (this.innerTableDiv && this.innerTableDiv.nativeElement) {
			this.innerTableDiv.nativeElement.scrollTop = 0;
		}
	}

	private isStaleSearchResponse(requestContext: { filter: string; pageNumber: number; sortField: string; sort: Sorting }): boolean {
		return (
			requestContext.filter !== this.pagingParams.filter ||
			requestContext.pageNumber !== this.pagingParams.pageNumber ||
			requestContext.sortField !== this.pagingParams.sortField ||
			requestContext.sort !== this.pagingParams.sort
		);
	}
}
